import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
    Activity, AlertTriangle, Camera, Compass, Crosshair, Gauge, Layers, MapPin, RotateCcw, Share2, Square, Trash2,
} from 'lucide-react';
import {
    gpsZustandAnzeige, useStandortSensoren, type EmpfangenePosition, type RichtungEreignis,
} from '../../services/standortSensoren';
import {
    druckMedian, geschosseAusMarken, geschosseLesen, geschossSchaetzen, gpsMitteln, hoehenDifferenzM,
    nullpunktAusGeschoss, useVerortungGeraet, useVerortungsSitzung, utmEingabeLesen, utmFormatieren, wgs84ZuUtm,
    type Ebene, type EbenenSchaetzung, type Geschoss, type Messmarke, type Nullpunkt, type SensorSchnappschuss, type Utm,
} from '../../services/verortung';
import type { ProjektVerortung, VerortungGeruest } from '../../types';
import { VerortungsKamera } from '../verortung/VerortungsKamera';
import {
    einstellungLaden, einstellungSpeichern, gleichesGeschoss, protokollAlsCsv, protokollLaden, protokollSpeichern,
    trefferquote, zeitText, type GeschossQuelle, type MessEinstellung, type MessEintrag,
} from '../../services/sensorMessprotokoll';
import { dateiAusgeben } from '../../services/dateiAusgabe';

/**
 * Sensor-Messmodus in „App-Info" (docs/maengel-verortung-plan.md 9.4, Paket
 * P5): Messpilot fuer die automatische Verortung, bevor die Oberflaeche im
 * Wizard gebaut wird.
 *
 *   • Live: Luftdruck (Median 10 s) → Hoehe relativ zum Nullpunkt →
 *     geschaetztes Geschoss; GPS mit Genauigkeit, Satelliten, UTM; Kompass.
 *   • „Hier bin ich": erster Tipp setzt den Nullpunkt, jeder weitere
 *     protokolliert Schaetzung gegen Wirklichkeit (Trefferquote) und nullt neu.
 *   • Geschosshoehen: eingetragen (aus dem Plan) ODER vor Ort gemessen.
 *   • Passpunkt: GPS 30 s mitteln, optional gegen UTM-Soll aus dem
 *     Absteckprotokoll → Abweichung in Metern.
 *
 * Sensoren laufen nur bei geoeffnetem Messmodus. Gespeichert werden nur die
 * gesetzten Punkte, nur auf diesem Geraet; Weitergabe per CSV.
 *
 * Bewusst keine `fixed`-Flaeche (siehe AppDownloadView): steht inline.
 */

const STANDARD_GESCHOSSE = ['DG', '5. OG', '4. OG', '3. OG', '2. OG', '1. OG', 'EG', 'UG'];
/** GPS-Fix gilt als aktuell, solange er juenger ist. */
const FIX_AKTUELL_MS = 10_000;
const PASSPUNKT_DAUER_MS = 30_000;
const KOMPASS_TEXT = ['unzuverlässig', 'niedrig', 'mittel', 'hoch'] as const;
const RICHTUNGEN = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'] as const;
/** Sitzung (Nullpunkt) des Messmodus — dieselbe nutzt die Kamera-Vorschau. */
const MESS_SITZUNG = 'messmodus';
const STANDARD_GERUEST: VerortungGeruest = { ersteLageM: 2, abstandM: 2, lagen: 6 };

const zahl = (w: number, stellen: number): string =>
    w.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });

const mitVorzeichen = (w: number, stellen: number): string => `${w >= 0 ? '+' : '−'}${zahl(Math.abs(w), stellen)}`;

const himmelsrichtung = (grad: number): string => RICHTUNGEN[Math.round((((grad % 360) + 360) % 360) / 45) % 8];

const neueId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const findeGeschoss = (liste: readonly Geschoss[], name: string): Geschoss | undefined =>
    liste.find((g) => gleichesGeschoss(g.name, name));

const Kachel: React.FC<{ titel: string; symbol: React.ReactNode; children: React.ReactNode }> = ({ titel, symbol, children }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1">{symbol}{titel}</p>
        {children}
    </div>
);

const Hinweis: React.FC<{ children: React.ReactNode; ton?: 'warnung' | 'info' }> = ({ children, ton = 'warnung' }) => (
    <p className={`mt-2 text-xs rounded-lg px-2.5 py-2 flex items-start gap-1.5 ${ton === 'warnung' ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-sky-50 text-sky-900 border border-sky-200'}`}>
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{children}</span>
    </p>
);

/** Werte, die Klick- und Zeitgeber-Handler zum Zeitpunkt des Auslösens brauchen. */
interface Aktuell {
    positionen: EmpfangenePosition[];
    richtung: RichtungEreignis | null;
    median: { hPa: number; streuungHpa: number } | null;
    nullpunkt: Nullpunkt | null;
    eintraege: MessEintrag[];
    /** Messmodus noch offen (u. a. Geräteschalter an)? Sonst bricht die Passpunkt-Messung ab. */
    offen: boolean;
}

export function SensorMessmodus(): JSX.Element {
    const [offenGewuenscht, setOffen] = useState(false);
    // Immer abschaltbar (Codex-Review 27.09., P1): Geräteschalter aus ⇒ Messmodus zu, keine Sensoren.
    const [geraetVerortung] = useVerortungGeraet();
    const offen = offenGewuenscht && geraetVerortung.aktiv;
    const sensoren = useStandortSensoren(offen);
    const [jetzt, setJetzt] = useState(0);
    const [einstellung, setEinstellung] = useState<MessEinstellung>(einstellungLaden);
    const [eintraege, setEintraege] = useState<MessEintrag[]>(protokollLaden);
    // Nullpunkt über die Verortungs-Sitzung (sessionStorage) — dieselbe liest die Kamera-Vorschau.
    const [sitzung, setSitzung] = useVerortungsSitzung(MESS_SITZUNG);
    const nullpunkt: Nullpunkt | null = sitzung.nullpunkt;
    const vorherigesId = sitzung.vorherigesId;
    const [kameraOffen, setKameraOffen] = useState(false);
    const [auswahl, setAuswahl] = useState('');
    const [neuNullen, setNeuNullen] = useState(true);
    const [passName, setPassName] = useState('');
    const [passSoll, setPassSoll] = useState('');
    const [passStart, setPassStart] = useState<number | null>(null);
    const [loeschenFragen, setLoeschenFragen] = useState(false);
    const [meldung, setMeldung] = useState<string | null>(null);
    const passTimer = useRef<number | null>(null);

    // Uhr nur bei offenem Messmodus: Median-Fenster, Fix-Alter, Countdown.
    useEffect(() => {
        if (!offen) return undefined;
        const tick = () => setJetzt(Date.now());
        const erster = window.setTimeout(tick, 0);
        const takt = window.setInterval(tick, 1000);
        return () => { window.clearTimeout(erster); window.clearInterval(takt); };
    }, [offen]);

    useEffect(() => () => { if (passTimer.current !== null) window.clearTimeout(passTimer.current); }, []);

    useEffect(() => { einstellungSpeichern(einstellung); }, [einstellung]);

    // Die Uhr tickt nur je Sekunde: Proben, die jünger sind als der letzte Tick, trotzdem mitnehmen.
    const median = useMemo(() => {
        const letzteProbe = sensoren.druckProben.length ? sensoren.druckProben[sensoren.druckProben.length - 1].t : 0;
        return druckMedian(sensoren.druckProben, Math.max(jetzt, letzteProbe), 10_000);
    }, [sensoren.druckProben, jetzt]);
    const eingabe = useMemo(() => geschosseLesen(einstellung.geschosseText), [einstellung.geschosseText]);

    // Vor Ort gemessene Geschosse: Punkte der laufenden Messreihe (seit dem letzten Nullpunkt-Eintrag).
    const marken = useMemo<Messmarke[]>(() => {
        let start = -1;
        eintraege.forEach((e, i) => { if (e.typ === 'nullpunkt') start = i; });
        if (start < 0) return [];
        return eintraege.slice(start)
            .filter((e) => (e.typ === 'nullpunkt' || e.typ === 'kontrolle') && e.geschoss && typeof e.hoeheM === 'number'
                && e.ebeneArt !== 'geruest')
            .map((e) => ({ geschossName: e.geschoss as string, hoeheM: e.hoeheM as number }));
    }, [eintraege]);
    const gemessen = useMemo(() => geschosseAusMarken(marken), [marken]);
    const liste = einstellung.quelle === 'eingabe' ? eingabe.geschosse : gemessen;

    const hoeheM = nullpunkt && median ? nullpunkt.hoeheM + hoehenDifferenzM(nullpunkt.druckHpa, median.hPa) : null;
    const schaetzung = nullpunkt && median && liste.length > 0
        ? geschossSchaetzen(liste, nullpunkt, median.hPa, jetzt, { vorherigesId })
        : null;

    const letzte = sensoren.positionen.length ? sensoren.positionen[sensoren.positionen.length - 1] : null;
    const fixAlterS = letzte ? Math.max(0, Math.round((jetzt - letzte.empfangen) / 1000)) : null;
    const fixAktuell = letzte !== null && fixAlterS !== null && fixAlterS * 1000 <= FIX_AKTUELL_MS;
    const utm = letzte ? wgs84ZuUtm(letzte.lat, letzte.lng) : null;
    const r = sensoren.richtung;

    const aktuell = useRef<Aktuell>({ positionen: [], richtung: null, median: null, nullpunkt: null, eintraege: [], offen: false });
    useEffect(() => {
        aktuell.current = { positionen: sensoren.positionen, richtung: sensoren.richtung, median, nullpunkt, eintraege, offen };
    });

    const quote = trefferquote(eintraege);

    // Auswahl-Chips: bekannte Geschosse von oben nach unten (wie im Aufzug), sonst die üblichen Namen.
    const chips = useMemo(() => {
        const bekannt = [...liste, ...(einstellung.quelle === 'messung' ? eingabe.geschosse : [])];
        const eindeutig: Geschoss[] = [];
        for (const g of bekannt) if (!findeGeschoss(eindeutig, g.name)) eindeutig.push(g);
        if (eindeutig.length === 0) return STANDARD_GESCHOSSE;
        return [...eindeutig].sort((a, b) => b.hoeheM - a.hoeheM).map((g) => g.name);
    }, [liste, eingabe.geschosse, einstellung.quelle]);

    const protokollSetzen = (neu: MessEintrag[]) => {
        setEintraege(neu);
        if (!protokollSpeichern(neu)) setMeldung('Protokoll konnte auf dem Gerät nicht gespeichert werden — vor dem Schließen als CSV teilen.');
    };

    const sensorFelder = (a: Aktuell, t: number): Partial<MessEintrag> => {
        const pos = a.positionen.length ? a.positionen[a.positionen.length - 1] : null;
        const posAktuell = pos && t - pos.empfangen <= FIX_AKTUELL_MS ? pos : null;
        const u = posAktuell ? wgs84ZuUtm(posAktuell.lat, posAktuell.lng) : null;
        return {
            druckHpa: a.median?.hPa ?? null,
            streuungHpa: a.median?.streuungHpa ?? null,
            lat: posAktuell?.lat ?? null,
            lng: posAktuell?.lng ?? null,
            genauigkeitM: posAktuell?.genauigkeitM ?? null,
            satelliten: posAktuell?.satelliten ?? null,
            zweiFrequenz: posAktuell?.zweiFrequenz ?? null,
            zone: u?.zone ?? null,
            ost: u?.ost ?? null,
            nord: u?.nord ?? null,
            azimutGrad: a.richtung?.azimutGrad ?? null,
            kompassGenauigkeit: a.richtung?.genauigkeit ?? null,
        };
    };

    const hierBinIch = () => {
        const name = auswahl.trim();
        const a = aktuell.current;
        if (!name) { setMeldung('Erst das Geschoss antippen oder eintragen.'); return; }
        if (!a.median) { setMeldung('Noch kein Luftdruck — ein paar Sekunden warten.'); return; }
        const t = Date.now();
        const basis = { id: neueId(), t, geschoss: name, ...sensorFelder(a, t) };

        if (!a.nullpunkt) {
            // Messreihe beginnt: eingetragene Höhe dieses Geschosses, bei Messung vor Ort ±0,00.
            const soll = einstellung.quelle === 'eingabe' ? findeGeschoss(eingabe.geschosse, name) : undefined;
            const h = soll?.hoeheM ?? 0;
            setSitzung({ ...sitzung, nullpunkt: { hoeheM: h, druckHpa: a.median.hPa, t }, vorherigesId: soll?.id ?? null, geruestModus: false });
            protokollSetzen([...eintraege, { ...basis, typ: 'nullpunkt', hoeheM: h }]);
            setMeldung(`Nullpunkt gesetzt: ${name} = ${mitVorzeichen(h, 2)} m.`);
            return;
        }

        const h = a.nullpunkt.hoeheM + hoehenDifferenzM(a.nullpunkt.druckHpa, a.median.hPa);
        const ziel = findeGeschoss(liste, name);
        const s = liste.length > 0 ? geschossSchaetzen(liste, a.nullpunkt, a.median.hPa, t, { vorherigesId }) : null;
        // Bewertbar nur, wenn das angegebene Geschoss in der Liste steht (bei Messung vor Ort: schon einmal markiert).
        const treffer = s && s.geschoss && ziel ? !s.zwischen && gleichesGeschoss(s.geschoss.name, name) : null;
        const nullen = neuNullen && !!ziel;
        setSitzung({
            ...sitzung,
            nullpunkt: nullen && ziel ? nullpunktAusGeschoss(ziel, a.median.hPa, t) : a.nullpunkt,
            vorherigesId: ziel?.id ?? null,
            geruestModus: false,
        });
        protokollSetzen([...eintraege, {
            ...basis,
            typ: 'kontrolle',
            hoeheM: h,
            geschaetzt: s?.geschoss?.name ?? null,
            zwischen: s?.zwischen ? `${s.zwischen[0].name} / ${s.zwischen[1].name}` : null,
            sicherheit: s?.sicherheit ?? null,
            treffer,
            nullpunktAlterMin: s?.nullpunktAlterMin ?? (t - a.nullpunkt.t) / 60_000,
            neuGenullt: nullen,
        }]);
        setMeldung(treffer === null
            ? `${name} markiert (${mitVorzeichen(h, 2)} m).`
            : treffer ? `Richtig erkannt: ${name}.` : `Abweichung: geschätzt ${s?.zwischen ? 'zwischen zwei Geschossen' : s?.geschoss?.name}, tatsächlich ${name}.`);
    };

    const neueMessreihe = () => {
        setSitzung({ ...sitzung, nullpunkt: null, vorherigesId: null, geruestModus: false });
        setMeldung('Neue Messreihe: das nächste „Hier bin ich" setzt den Nullpunkt.');
    };

    const passpunktMessen = () => {
        if (passStart !== null) return;
        let soll: Utm | null = null;
        if (passSoll.trim()) {
            const gelesen = utmEingabeLesen(passSoll);
            if ('fehler' in gelesen) { setMeldung(`Soll-Koordinate: ${gelesen.fehler}`); return; }
            soll = gelesen;
        }
        const start = Date.now();
        setPassStart(start);
        setMeldung(null);
        passTimer.current = window.setTimeout(() => {
            passTimer.current = null;
            setPassStart(null);
            const a = aktuell.current;
            if (!a.offen) {
                setMeldung('Passpunkt-Messung abgebrochen: Messmodus geschlossen oder Sensor-Verortung abgeschaltet.');
                return;
            }
            const fixe = a.positionen
                .filter((p) => p.empfangen >= start)
                .map((p) => ({ lat: p.lat, lng: p.lng, genauigkeitM: p.genauigkeitM, t: p.empfangen }));
            const mittel = gpsMitteln(fixe, soll ? { zone: soll.zone } : undefined);
            if (!mittel) {
                setMeldung('Kein verwertbarer GPS-Fix in 30 s — unter einer Decke? GPS aus? Freie Sicht zum Himmel suchen.');
                return;
            }
            const u: Utm = soll ? wgs84ZuUtm(mittel.lat, mittel.lng, soll.zone) : mittel.utm;
            const abweichung = soll ? Math.hypot(u.ost - soll.ost, u.nord - soll.nord) : null;
            const t = Date.now();
            const h = a.nullpunkt && a.median ? a.nullpunkt.hoeheM + hoehenDifferenzM(a.nullpunkt.druckHpa, a.median.hPa) : null;
            // Liste aus der Ref (Stand des letzten Renders), nicht aus der Closure vom Klick: während
            // der 30 s kann „Hier bin ich" weitere Punkte angelegt haben.
            const alt = a.eintraege;
            const neu: MessEintrag[] = [...alt, {
                ...sensorFelder(a, t),
                id: neueId(),
                typ: 'passpunkt',
                t,
                name: passName.trim() || `Passpunkt ${alt.filter((e) => e.typ === 'passpunkt').length + 1}`,
                hoeheM: h,
                lat: mittel.lat,
                lng: mittel.lng,
                genauigkeitM: mittel.genauigkeitM,
                streuungM: mittel.streuungM,
                anzahl: mittel.anzahl,
                zone: u.zone,
                ost: u.ost,
                nord: u.nord,
                sollOst: soll?.ost ?? null,
                sollNord: soll?.nord ?? null,
                abweichungM: abweichung,
            }];
            const gespeichert = protokollSpeichern(neu);
            setEintraege(neu);
            const ergebnis = abweichung === null
                ? `Passpunkt gemessen: ±${zahl(mittel.genauigkeitM, 1)} m aus ${mittel.anzahl} Fixen.`
                : `Passpunkt gemessen: Abweichung zum Soll ${zahl(abweichung, 2)} m (±${zahl(mittel.genauigkeitM, 1)} m, ${mittel.anzahl} Fixe).`;
            // Codex-Review 27.09.: Speicherfehler sichtbar machen statt Erfolg zu melden.
            setMeldung(gespeichert ? ergebnis : `${ergebnis} Achtung: auf dem Gerät nicht gespeichert — vor dem Schließen als CSV teilen.`);
            setPassName('');
        }, PASSPUNKT_DAUER_MS);
    };

    const passpunktAbbrechen = () => {
        if (passTimer.current !== null) window.clearTimeout(passTimer.current);
        passTimer.current = null;
        setPassStart(null);
    };

    const messmodusSchliessen = () => {
        passpunktAbbrechen();
        setKameraOffen(false);
        setOffen(false);
    };

    // ─── Kamera-Vorschau (dieselbe Einblendung wie im Mangel-Wizard) ─────────
    const kameraVerortung = useMemo<ProjektVerortung>(
        () => ({ aktiv: true, geschosse: liste, geruest: einstellung.geruest ?? null, plaene: {} }),
        [liste, einstellung.geruest],
    );

    /** Chip in der Kamera = „Hier bin ich“: ins Protokoll, wie der Knopf unten. */
    const kameraBestaetigt = (ebene: Ebene, schnapp: SensorSchnappschuss, vorher: EbenenSchaetzung | null) => {
        const a = aktuell.current;
        const t = schnapp.t;
        const treffer = vorher && vorher.ebene ? !vorher.zwischen && gleichesGeschoss(vorher.ebene.name, ebene.name) : null;
        const eintrag: MessEintrag = {
            ...sensorFelder(a, t),
            id: neueId(),
            t,
            typ: vorher ? 'kontrolle' : 'nullpunkt',
            geschoss: ebene.name,
            ebeneArt: ebene.art,
            druckHpa: schnapp.druckHpa ?? a.median?.hPa ?? null,
            hoeheM: vorher ? vorher.hoeheM : ebene.hoeheM,
            geschaetzt: vorher?.ebene?.name ?? null,
            zwischen: vorher?.zwischen ? `${vorher.zwischen[0].name} / ${vorher.zwischen[1].name}` : null,
            sicherheit: vorher?.sicherheit ?? null,
            treffer,
            nullpunktAlterMin: vorher?.nullpunktAlterMin ?? null,
            neuGenullt: true,
        };
        protokollSetzen([...a.eintraege, eintrag]);
    };

    // Die Kamera schreibt dieselbe Sitzung fort (gemeinsamer Stand je Schlüssel) — kein Neu-Einlesen nötig.
    const kameraSchliessen = () => setKameraOffen(false);

    const geruestSetzen = (patch: Partial<VerortungGeruest> | null) => setEinstellung((e) => ({
        ...e,
        geruest: patch === null ? null : { ...(e.geruest ?? STANDARD_GERUEST), ...patch },
    }));

    const csvTeilen = async () => {
        const p = sensoren.pruefung;
        const kopf = [
            `Sensor-Messprotokoll SiGeKo, erstellt ${zeitText(Date.now())}`,
            `Gerät: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
            `Barometer: ${p ? (p.barometer ? p.barometerName ?? 'ja' : 'keins') : 'unbekannt'}; Kompass: ${p?.kompass ?? 'unbekannt'}`,
            `Geschosshöhen aus: ${einstellung.quelle === 'eingabe' ? 'Eingabe (Plan)' : 'Messung vor Ort'}; Eingabe: ${einstellung.geschosseText.replace(/\s*\n\s*/g, '; ')}`,
        ];
        const csv = protokollAlsCsv(eintraege, kopf);
        const datum = zeitText(Date.now()).slice(0, 10);
        try {
            await dateiAusgeben(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `sensor-messprotokoll-${datum}.csv`);
        } catch (e) {
            setMeldung(`Teilen fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const protokollLoeschen = () => {
        protokollSetzen([]);
        setSitzung({ ...sitzung, nullpunkt: null, vorherigesId: null, geruestModus: false });
        setLoeschenFragen(false);
        setMeldung('Protokoll gelöscht.');
    };

    const quelleSetzen = (quelle: GeschossQuelle) => setEinstellung((e) => ({ ...e, quelle }));

    // ─── Geschlossen ─────────────────────────────────────────────────────────
    if (!offen) {
        return (
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-sky-600" /> Sensor-Messmodus (Pilot Verortung)
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                    Misst auf der Baustelle Luftdruck, GPS und Kompass, um die automatische Verortung von Mängeln zu erproben:
                    Wie oft stimmt das Geschoss, wie genau ist GPS auf der Decke? Sensoren laufen nur, solange der Messmodus offen ist.
                    Gespeichert werden nur die Punkte, die Sie selbst setzen — nur auf diesem Gerät.
                </p>
                {eintraege.length > 0 && (
                    <p className="mt-2 text-xs text-slate-500">{eintraege.length} Messpunkte im Protokoll.</p>
                )}
                {!geraetVerortung.aktiv && (
                    <p className="mt-2 text-xs text-slate-500">
                        Ausgeschaltet: „Sensor-Verortung auf diesem Gerät“ (oben) ist aus.
                    </p>
                )}
                <button
                    type="button"
                    onClick={() => { setMeldung(null); setOffen(true); }}
                    disabled={!geraetVerortung.aktiv}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-700 active:bg-sky-800 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-3 transition-colors"
                >
                    <Activity className="w-4 h-4" /> Messmodus öffnen
                </button>
            </section>
        );
    }

    const p = sensoren.pruefung;
    const gpsZustand = gpsZustandAnzeige(sensoren.status, p);
    const verbleibendS = passStart !== null ? Math.max(0, Math.ceil((passStart + PASSPUNKT_DAUER_MS - jetzt) / 1000)) : null;

    return (
        <section className="bg-white rounded-2xl border border-sky-200 shadow-sm p-4 sm:p-5 space-y-4">
            <header className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-sky-600" /> Sensor-Messmodus
                </h2>
                <button
                    type="button"
                    onClick={messmodusSchliessen}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                >
                    <Square className="w-3.5 h-3.5" /> Beenden
                </button>
            </header>

            {!sensoren.verfuegbar && (
                <Hinweis>Diese App-Version kennt die Sensoren noch nicht. Bitte die App aktualisieren (ab Version 1.0.2).</Hinweis>
            )}
            {sensoren.fehler && <Hinweis>{sensoren.fehler}</Hinweis>}
            {meldung && (
                <p className="text-sm rounded-lg bg-slate-800 text-white px-3 py-2" role="status">{meldung}</p>
            )}

            {sensoren.verfuegbar && (
                <>
                    {/* ─── Live-Werte ─────────────────────────────────────── */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Kachel titel="Geschoss / Höhe" symbol={<Gauge className="w-3.5 h-3.5" />}>
                            {p && !p.barometer ? (
                                <p className="text-sm text-slate-700">Kein Luftdrucksensor in diesem Gerät — Geschoss nicht bestimmbar.</p>
                            ) : (
                                <>
                                    <p className="text-2xl font-bold text-slate-900 leading-tight">
                                        {schaetzung?.zwischen
                                            ? `${schaetzung.zwischen[0].name} ↔ ${schaetzung.zwischen[1].name}`
                                            : schaetzung?.geschoss?.name ?? (nullpunkt ? '—' : 'Nullpunkt fehlt')}
                                    </p>
                                    <p className="text-sm text-slate-700">
                                        {hoeheM !== null ? `${mitVorzeichen(hoeheM, 2)} m` : '— m'}
                                        {schaetzung && !schaetzung.zwischen && ` · Sicherheit ${Math.round(schaetzung.sicherheit * 100)} %`}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {median ? `${zahl(median.hPa, 2)} hPa ± ${zahl(median.streuungHpa, 3)} · ${median.anzahl} Proben/10 s` : 'Luftdruck wird gelesen …'}
                                    </p>
                                    {nullpunkt && schaetzung && (
                                        <p className="text-xs text-slate-500">
                                            Nullpunkt vor {zahl(schaetzung.nullpunktAlterMin, 0)} min · Unsicherheit ±{zahl(schaetzung.unsicherheitM, 1)} m
                                        </p>
                                    )}
                                </>
                            )}
                        </Kachel>

                        <Kachel titel="GPS" symbol={<MapPin className="w-3.5 h-3.5" />}>
                            {gpsZustand === 'nicht_vorhanden' ? (
                                <p className="text-sm text-slate-700">Kein GPS in diesem Gerät.</p>
                            ) : gpsZustand === 'keine_erlaubnis' ? (
                                <>
                                    <p className="text-sm text-slate-700">Genauer Standort ist nicht freigegeben.</p>
                                    <button
                                        type="button"
                                        onClick={() => { void sensoren.erlaubnisAnfragen(); }}
                                        className="mt-2 rounded-lg bg-sky-600 text-white text-xs font-semibold px-3 py-2"
                                    >
                                        Genauen Standort erlauben
                                    </button>
                                    <p className="mt-2 text-xs text-slate-500">
                                        Erscheint kein Dialog: Android-Einstellungen → Apps → diese App → Berechtigungen → Standort →
                                        „Nur bei Nutzung der App“ und „Genauen Standort verwenden“ einschalten.
                                    </p>
                                </>
                            ) : gpsZustand === 'aus' ? (
                                <p className="text-sm text-slate-700">GPS ist aus — in den Android-Schnelleinstellungen „Standort" einschalten.</p>
                            ) : letzte ? (
                                <>
                                    <p className={`text-2xl font-bold leading-tight ${fixAktuell ? 'text-slate-900' : 'text-slate-400'}`}>
                                        ±{zahl(letzte.genauigkeitM, 1)} m
                                    </p>
                                    <p className="text-sm text-slate-700">
                                        {letzte.satelliten !== null ? `${letzte.satelliten} Sat.` : 'Sat. ?'}
                                        {' · Zweifrequenz '}{letzte.zweiFrequenz === null ? '?' : letzte.zweiFrequenz ? 'ja' : 'nein'}
                                    </p>
                                    {utm && <p className="text-xs text-slate-500 mt-1 font-mono">{utmFormatieren(utm, 1)}</p>}
                                    <p className="text-xs text-slate-500">
                                        {fixAktuell ? `Fix vor ${fixAlterS} s` : `Kein aktueller Fix (seit ${fixAlterS} s) — unter einer Decke?`}
                                        {letzte.hoeheM !== null && ` · Ellipsoidhöhe ${zahl(letzte.hoeheM, 0)} m`}
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-slate-500">Warte auf GPS-Fix … (draußen mit freier Sicht zum Himmel)</p>
                            )}
                        </Kachel>

                        <Kachel titel="Kompass" symbol={<Compass className="w-3.5 h-3.5" />}>
                            {p?.kompass === 'keiner' ? (
                                <p className="text-sm text-slate-700">Kein Kompass in diesem Gerät.</p>
                            ) : r ? (
                                <>
                                    <p className="text-2xl font-bold text-slate-900 leading-tight">
                                        {zahl(r.azimutGrad, 0)}° {himmelsrichtung(r.azimutGrad + (r.deklinationGrad ?? 0))}
                                    </p>
                                    <p className="text-sm text-slate-700">
                                        {r.achse === 'kamera' ? 'Blick der Kamera' : 'Oberkante (liegt flach)'} · {KOMPASS_TEXT[r.genauigkeit]}
                                        {r.fehlerGrad !== null && ` ±${zahl(r.fehlerGrad, 0)}°`}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        magnetisch{r.deklinationGrad !== null && `, Deklination ${mitVorzeichen(r.deklinationGrad, 1)}°`}
                                        {r.achse === 'kamera' && ` · Neigung ${mitVorzeichen(r.neigungGrad, 0)}°`}
                                    </p>
                                    {r.genauigkeit <= 1 && (
                                        <Hinweis ton="info">Gerät in Form einer 8 schwenken; Abstand zu Stahl, Bewehrung und Kran halten.</Hinweis>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-slate-500">Kompass wird gelesen …</p>
                            )}
                        </Kachel>
                    </div>

                    {/* ─── Hier bin ich ───────────────────────────────────── */}
                    <div className="rounded-xl border border-slate-200 p-3">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Layers className="w-4 h-4 text-sky-600" /> Hier bin ich
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {nullpunkt
                                ? 'Geschoss antippen und bestätigen — das Protokoll vergleicht mit der Schätzung.'
                                : 'Erster Tipp setzt den Nullpunkt (z. B. im EG).'}
                        </p>
                        <button
                            type="button"
                            onClick={() => setKameraOffen(true)}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-sky-600 text-sky-700 bg-white active:bg-sky-50 text-sm font-semibold px-4 py-2.5"
                        >
                            <Camera className="w-4 h-4" /> Kamera-Ansicht mit Einblendung (Vorschau)
                        </button>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {chips.map((name) => (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => setAuswahl(name)}
                                    className={`min-w-[3.5rem] rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${gleichesGeschoss(auswahl, name) ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'}`}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            value={auswahl}
                            onChange={(e) => setAuswahl(e.target.value)}
                            placeholder="oder eintragen, z. B. „Gerüst 3. Lage“"
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        {nullpunkt && (
                            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                                <input type="checkbox" checked={neuNullen} onChange={(e) => setNeuNullen(e.target.checked)} />
                                Danach neu nullen (so soll es im Betrieb laufen: jede Bestätigung gleicht den Wetterdrift aus)
                            </label>
                        )}
                        <div className="mt-3 flex gap-2">
                            <button
                                type="button"
                                onClick={hierBinIch}
                                disabled={!median}
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-700 active:bg-sky-800 disabled:bg-slate-300 text-white text-base font-semibold px-4 py-3.5 transition-colors"
                            >
                                <Crosshair className="w-5 h-5" />
                                {auswahl.trim() ? `Hier bin ich: ${auswahl.trim()}` : 'Hier bin ich'}
                            </button>
                            {nullpunkt && (
                                <button
                                    type="button"
                                    onClick={neueMessreihe}
                                    title="Neue Messreihe"
                                    className="rounded-xl border border-slate-300 px-3 text-slate-600 active:bg-slate-100"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ─── Geschosshöhen ──────────────────────────────────── */}
                    <div className="rounded-xl border border-slate-200 p-3">
                        <p className="text-sm font-semibold text-slate-800">Geschosshöhen</p>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            {(['eingabe', 'messung'] as const).map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => quelleSetzen(q)}
                                    className={`rounded-lg border px-3 py-2 text-xs font-semibold ${einstellung.quelle === q ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700'}`}
                                >
                                    {q === 'eingabe' ? 'Aus dem Plan eingetragen' : 'Vor Ort gemessen'}
                                </button>
                            ))}
                        </div>
                        {einstellung.quelle === 'eingabe' ? (
                            <>
                                <textarea
                                    value={einstellung.geschosseText}
                                    onChange={(e) => setEinstellung((alt) => ({ ...alt, geschosseText: e.target.value }))}
                                    rows={4}
                                    placeholder={'Höhe relativ ±0,00 (OKFF EG), eine Zeile je Geschoss:\nUG -3,20\nEG 0\n1. OG 3,05\n2. OG 6,10'}
                                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                                />
                                {eingabe.fehler.map((f) => <p key={f} className="text-xs text-rose-700">{f}</p>)}
                            </>
                        ) : (
                            <p className="mt-2 text-xs text-slate-600">
                                {gemessen.length
                                    ? gemessen.map((g) => `${g.name} ${mitVorzeichen(g.hoeheM, 2)} m`).join(' · ')
                                    : 'Noch nichts gemessen: im Bezugsgeschoss „Hier bin ich“ (= ±0,00), dann jedes Geschoss einmal markieren.'}
                            </p>
                        )}
                        <div className="mt-3 border-t border-slate-100 pt-3">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={!!einstellung.geruest}
                                    onChange={(e) => geruestSetzen(e.target.checked ? {} : null)}
                                />
                                Gerüst (für die Kamera-Ansicht)
                            </label>
                            {einstellung.geruest && (
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    <label className="text-xs text-slate-500">1. Lage (m)
                                        <input type="number" step="0.1" inputMode="decimal" value={einstellung.geruest.ersteLageM}
                                            onChange={(e) => { const w = Number(e.target.value); if (Number.isFinite(w)) geruestSetzen({ ersteLageM: w }); }}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                                    </label>
                                    <label className="text-xs text-slate-500">Abstand (m)
                                        <input type="number" step="0.1" min="0.5" inputMode="decimal" value={einstellung.geruest.abstandM}
                                            onChange={(e) => { const w = Number(e.target.value); if (Number.isFinite(w) && w > 0) geruestSetzen({ abstandM: w }); }}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                                    </label>
                                    <label className="text-xs text-slate-500">Lagen
                                        <input type="number" step="1" min="1" max="40" inputMode="numeric" value={einstellung.geruest.lagen}
                                            onChange={(e) => { const w = Math.round(Number(e.target.value)); if (Number.isFinite(w) && w >= 1) geruestSetzen({ lagen: Math.min(40, w) }); }}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ─── Passpunkt ──────────────────────────────────────── */}
                    <div className="rounded-xl border border-slate-200 p-3">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-sky-600" /> Passpunkt messen (GPS 30 s)
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Auf einen eindeutigen Punkt stellen (Gebäudeecke, Achskreuz), still halten. Mit UTM-Soll aus dem
                            Absteckprotokoll zeigt das Protokoll die echte GPS-Abweichung.
                        </p>
                        <input
                            type="text"
                            value={passName}
                            onChange={(e) => setPassName(e.target.value)}
                            placeholder="Name, z. B. „Achse A/1“"
                            disabled={passStart !== null}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        <input
                            type="text"
                            value={passSoll}
                            onChange={(e) => setPassSoll(e.target.value)}
                            placeholder="UTM-Soll (optional), z. B. 32 565123,45 5934567,89"
                            disabled={passStart !== null}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                        />
                        {passStart === null ? (
                            <button
                                type="button"
                                onClick={passpunktMessen}
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-sky-600 text-sky-700 bg-white active:bg-sky-50 text-sm font-semibold px-4 py-3"
                            >
                                <Crosshair className="w-4 h-4" /> 30 s messen
                            </button>
                        ) : (
                            <div className="mt-3 flex items-center gap-2">
                                <div className="flex-1 h-3 rounded-full bg-slate-200 overflow-hidden">
                                    <div
                                        className="h-full bg-sky-600 transition-all"
                                        style={{ width: `${Math.min(100, ((PASSPUNKT_DAUER_MS / 1000 - (verbleibendS ?? 0)) / (PASSPUNKT_DAUER_MS / 1000)) * 100)}%` }}
                                    />
                                </div>
                                <span className="text-sm font-semibold text-slate-700 w-12 text-right">{verbleibendS} s</span>
                                <button type="button" onClick={passpunktAbbrechen} className="text-xs text-slate-600 underline">Abbrechen</button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {kameraOffen && offen && (
                <VerortungsKamera
                    verortung={kameraVerortung}
                    plaene={[]}
                    sitzungSchluessel={MESS_SITZUNG}
                    titel="Messmodus — Kamera-Vorschau"
                    onBestaetigt={kameraBestaetigt}
                    onSchliessen={kameraSchliessen}
                />
            )}

            {/* ─── Protokoll ──────────────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">Protokoll ({eintraege.length})</p>
                {quote.bewertet > 0 && (
                    <p className="mt-1 text-sm text-slate-700">
                        Geschoss richtig: <strong>{quote.treffer} von {quote.bewertet}</strong> ({Math.round((quote.treffer / quote.bewertet) * 100)} %)
                        {quote.zwischen > 0 && ` · ${quote.zwischen}× „zwischen“`}
                    </p>
                )}
                <ul className="mt-2 divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {[...eintraege].reverse().slice(0, 30).map((e) => (
                        <li key={e.id} className="py-1.5 text-xs text-slate-700">
                            <span className="text-slate-400 font-mono">{zeitText(e.t).slice(11)}</span>{' '}
                            {e.typ === 'nullpunkt' && <>Nullpunkt <strong>{e.geschoss}</strong> ({mitVorzeichen(e.hoeheM ?? 0, 2)} m)</>}
                            {e.typ === 'kontrolle' && (
                                <>
                                    <strong>{e.geschoss}</strong> {typeof e.hoeheM === 'number' && `${mitVorzeichen(e.hoeheM, 2)} m`}
                                    {e.treffer === true && <span className="text-emerald-700"> ✓</span>}
                                    {e.treffer === false && <span className="text-rose-700"> ✗ geschätzt {e.zwischen ?? e.geschaetzt}</span>}
                                </>
                            )}
                            {e.typ === 'passpunkt' && (
                                <>
                                    Passpunkt <strong>{e.name}</strong> ±{zahl(e.genauigkeitM ?? 0, 1)} m
                                    {typeof e.abweichungM === 'number' && ` · Abweichung ${zahl(e.abweichungM, 2)} m`}
                                </>
                            )}
                            {e.typ !== 'passpunkt' && typeof e.genauigkeitM === 'number' && <span className="text-slate-400"> · GPS ±{zahl(e.genauigkeitM, 0)} m</span>}
                        </li>
                    ))}
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => { void csvTeilen(); }}
                        disabled={eintraege.length === 0}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-800 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2.5"
                    >
                        <Share2 className="w-4 h-4" /> Als CSV teilen
                    </button>
                    {!loeschenFragen ? (
                        <button
                            type="button"
                            onClick={() => setLoeschenFragen(true)}
                            disabled={eintraege.length === 0}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 disabled:text-slate-300 text-slate-700 text-sm font-semibold px-4 py-2.5"
                        >
                            <Trash2 className="w-4 h-4" /> Löschen
                        </button>
                    ) : (
                        <div className="w-full flex gap-2">
                            <button type="button" onClick={protokollLoeschen} className="flex-1 rounded-xl bg-rose-600 text-white text-sm font-semibold px-4 py-2.5">
                                Wirklich löschen
                            </button>
                            <button type="button" onClick={() => setLoeschenFragen(false)} className="flex-1 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold px-4 py-2.5">
                                Abbrechen
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
